import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { markSkipOnCompleteIfOnSuccessIsSet, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { chain, ExecuteOnSuccessCallback } from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { CartLineModel, OrderLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { setGA4Event } from "@tggstore/Handlers/Utilities/CustomTracking";
import { getProductCategories } from "@tggstore/Services/CommonServices";
import { CustomState } from "@tggstore/Store/Reducers";

replaceInChain(chain, ExecuteOnSuccessCallback, async props => {
    const state = props.getState();
    const isProductListPage = getCurrentPage(state).type === "ProductListPage";

    if (!props.result?.errorMessage && props.parameter.onSuccess) {
        const currentPath = window.location.pathname;
        if (currentPath.includes("/MyAccount/Orders/Details")) {
            isOrderPage(state, props.parameter.productInfos, props);
        } else if (currentPath.includes("/MyAccount/SavedOrders/Details")) {
            isSaveOrderPage(state, props.parameter.productInfos, props);
        } else {
            let products: Array<{ product: ProductModel; productInfo: Omit<ProductInfo, "productDetailPath"> }> = [];
            if (props.parameter.productInfos) {
                products = props.parameter.productInfos
                    .map(productInfo => {
                        const productState = getProductState(state, productInfo.productId);
                        if (productState?.value) {
                            return {
                                product: productState.value,
                                productInfo,
                            };
                        }
                        return null;
                    })
                    .filter(
                        (
                            product,
                        ): product is { product: ProductModel; productInfo: Omit<ProductInfo, "productDetailPath"> } =>
                            product !== null,
                    );
            }

            if (products.length > 0) {
                const state = props.getState() as CustomState;
                const session = props.getState().context.session;
                const currency = session?.currency?.currencyCode || "";
                const affiliation = state.customDefaultWarehouse.warehouse?.description || "";

                const billToCustomerName = getBillToState(state, session.billToId)?.value?.customerNumber ?? "";

                const totalPrice = parseFloat(
                    products
                        .reduce((acc, product) => {
                            const price =
                                (product.productInfo.pricing?.actualPrice ?? 0) * (product.productInfo.qtyOrdered ?? 0);
                            return acc + price;
                        }, 0)
                        .toFixed(2),
                );

                const productIds = products
                    .map(p => p.product.id)
                    .filter(id => id !== null && id !== undefined) as string[];

                await getProductCategories({
                    productIds,
                    type: "product",
                }).then((data: any) => {
                    const productCategories = data.result;
                    let indexes;
                    if (isProductListPage) {
                        indexes = getAttributes(productIds);
                    }

                    const purchasedCardIndex = localStorage.getItem("card-index");
                    if (purchasedCardIndex !== null && purchasedCardIndex !== "") {
                        const item = purchasedCardIndex.split("_");

                        if (item && item.length === 2) {
                            indexes = [
                                {
                                    id: item[0],
                                    attribute: item[1],
                                },
                            ];
                        }

                        // Clear after use
                        localStorage.removeItem("card-index");
                    }

                    setGA4Event(products, "add_to_wishlist", {
                        currency,
                        affiliation,
                        account_number: billToCustomerName,
                        value: totalPrice,
                        productCategories,
                        indexes,
                    });
                });
            }
        }

        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess(props.result.wishList!);
    }
});

async function isOrderPage(state: any, productInfos: Omit<ProductInfo, "productDetailPath">[], props: any) {
    const orderNumber = window.location.search.split("=")[1];
    const orderState = getOrderState(state, orderNumber);
    let products: OrderLineModel[] = [];

    if (orderState) {
        products = orderState.value?.orderLines ?? [];
        if (products) {
            const updatedArray = products.map((item, index) => ({
                ...item,
                lineNumber: index, // Add 1 if you want lineNumber to start from 1
            }));
            products = updatedArray.filter(p => productInfos.some(pi => pi.productId === p.productId));
        }
    }

    if (products != null && products.length > 0) {
        const state = props.getState() as CustomState;
        const session = props.getState().context.session;
        const currency = session?.currency?.currencyCode || "";
        const affiliation = state.customDefaultWarehouse.warehouse?.description || "";

        const billToCustomerName = getBillToState(state, session.billToId)?.value?.customerNumber ?? "";

        const totalPrice = parseFloat(
            products
                .reduce((acc, product) => {
                    const price = product.lineTotal ?? 0;
                    return acc + price;
                }, 0)
                .toFixed(2),
        );

        const productIds = products.map(p => p.productId).filter(id => id !== null && id !== undefined) as string[];

        await getProductCategories({
            productIds,
            type: "product",
        }).then((data: any) => {
            const productCategories = data.result;
            let indexes;

            setGA4Event(products, "add_to_wishlist", {
                currency,
                affiliation,
                account_number: billToCustomerName,
                value: totalPrice,
                productCategories,
                indexes,
            });
        });
    }
}

async function isSaveOrderPage(state: any, productInfos: Omit<ProductInfo, "productDetailPath">[], props: any) {
    const orderNumber = window.location.search.split("=")[1];
    const orderState = getCartState(state, orderNumber);
    let cartlines: Array<{ product: CartLineModel; productInfo: Omit<ProductInfo, "productDetailPath"> }> = [];
    if (orderState) {
        const cartLines = orderState.value?.cartLines;
        if (cartLines) {
            const updatedArray = cartLines.map((item, index) => ({
                ...item,
                line: index, // Add 1 if you want lineNumber to start from 1
            }));
            cartlines = productInfos
                .map(productInfo => {
                    const cartLineState = updatedArray.filter(p => productInfo.productId === p.productId);
                    productInfo = { ...productInfo, pricing: cartLineState[0].pricing ?? undefined };
                    if (cartLineState.length > 0) {
                        return {
                            product: cartLineState[0],
                            productInfo,
                        };
                    }
                    return null;
                })
                .filter(
                    (
                        cartline,
                    ): cartline is {
                        product: { line: number } & CartLineModel;
                        productInfo: Omit<ProductInfo, "productDetailPath">;
                    } => cartline !== null,
                );
        }
    }

    if (cartlines != null && cartlines.length > 0) {
        const state = props.getState() as CustomState;
        const session = props.getState().context.session;
        const currency = session?.currency?.currencyCode || "";
        const affiliation = state.customDefaultWarehouse.warehouse?.description || "";

        const billToCustomerName = getBillToState(state, session.billToId)?.value?.customerNumber ?? "";

        const totalPrice = parseFloat(
            cartlines
                .reduce((acc, cartLine) => {
                    const price = (cartLine.product.pricing?.actualPrice ?? 0) * (cartLine.product.qtyOrdered ?? 0);
                    return acc + price;
                }, 0)
                .toFixed(2),
        );

        const productIds = cartlines
            .map(p => p.product.productId)
            .filter(id => id !== null && id !== undefined) as string[];

        await getProductCategories({
            productIds,
            type: "product",
        }).then((data: any) => {
            const productCategories = data.result;

            setGA4Event(cartlines, "add_to_wishlist", {
                currency,
                affiliation,
                account_number: billToCustomerName,
                value: totalPrice,
                productCategories,
            });
        });
    }
}

// Function to get attributes from spans
const getAttributes = (ids: any) => {
    return ids
        .map((id: any) => {
            const element = document.getElementById(id);
            if (element) {
                return {
                    id,
                    attribute: element.getAttribute("data-index"),
                };
            }
            return null;
        })
        .filter(Boolean);
};
